import { ChannelHealthEntity, ChannelHealthPendingIssue } from 'lib-trend-core';

type TranslateFn = (key: string, params?: object) => string;

/**
 * Provider-wording i18n (SPEC-2026-08-channel-health, RNF-06).
 *
 * Pending-issue titles and entity reasons arrive in the API's stored form — our own fixed
 * texts (English source) or Meta's verbatim sentences. Translation resolves in order:
 *   1. Meta error_code (`reasonCode`) with a known catalog entry;
 *   2. exact match of a known Meta sentence (readings older than the reasonCode field);
 *   3. the issue's own `code`, for issues whose title is fixed by the platform;
 *   4. the original text — an unknown provider wording is shown, never hidden.
 */

/** Meta error codes with catalog entries (channel.health.metaError.{code}). */
const KNOWN_META_ERROR_CODES = new Set([131042, 131048, 131031]);

/** Known Meta sentences → catalog suffix, for readings that carry no reasonCode. */
const KNOWN_META_TEXTS: Record<string, string> = {
  'Your display name has not been approved yet. Your message limit will increase after the display name is approved.': 'NAME_NOT_APPROVED',
  'The Business has not passed business verification.': 'BUSINESS_NOT_VERIFIED',
  'Business eligibility payment issue': '131042',
  'Message failed to send because there are restrictions on how many messages can be sent from this phone number': '131048',
};

/** Issue codes whose title is a fixed platform text (safe to translate by code). */
const FIXED_TITLE_CODES = new Set([
  'BILLING_BLOCKED', 'BILLING_ISSUE', 'QUALITY_LOW', 'QUALITY_MEDIUM',
  'BUSINESS_VERIFICATION_REJECTED', 'NAME_REJECTED', 'ACCESS_LOST', 'MISSING_CREDENTIALS',
]);

function translateMetaWording(t: TranslateFn, text?: string, reasonCode?: number): string | undefined {
  if (reasonCode && KNOWN_META_ERROR_CODES.has(reasonCode)) {
    return t(`channel.health.metaError.${reasonCode}`);
  }
  const suffix = text ? KNOWN_META_TEXTS[text.trim()] : undefined;
  return suffix ? t(`channel.health.metaError.${suffix}`) : undefined;
}

export function translateIssueTitle(t: TranslateFn, issue: ChannelHealthPendingIssue): string {
  const metaWording = translateMetaWording(t, issue.title, issue.reasonCode);
  if (metaWording) return metaWording;

  if (FIXED_TITLE_CODES.has(issue.code)) {
    return t(`channel.health.issue.${issue.code}`);
  }
  if (issue.code === 'CREDENTIAL_EXPIRING') {
    const days = issue.title.match(/\d+/)?.[0] ?? '?';
    return t('channel.health.issue.CREDENTIAL_EXPIRING', { days });
  }
  // SEND_BLOCKED / SEND_LIMITED / ENTITY_* without a recognized reason: generic label only
  // when there is no provider text at all — a specific reason beats a generic translation.
  if ((issue.code === 'SEND_BLOCKED' || issue.code === 'SEND_LIMITED') && issue.title.startsWith('Sending ')) {
    return t(`channel.health.issue.${issue.code}`);
  }
  return issue.title;
}

export function translateEntityReason(t: TranslateFn, entity: ChannelHealthEntity): string | undefined {
  if (!entity.reason) return undefined;
  return translateMetaWording(t, entity.reason, entity.reasonCode) ?? entity.reason;
}

export function translateIssueCategory(t: TranslateFn, category: string): string {
  const known = ['NUMBER', 'PHONE_NUMBER', 'WABA', 'BUSINESS', 'APP', 'BILLING', 'CREDENTIAL', 'MESSAGE_TEMPLATE'];
  const normalized = (category ?? '').toUpperCase();
  return known.includes(normalized) ? t(`channel.health.issueCategory.${normalized}`) : category;
}
